import { array, object, string, type StringSchema } from "yup"
import { ALL_COUNTRIES, USA_COUNTRY_CODE } from "../../util/country"
import { isNilOrEmpty } from "../../util/string"
import { ZIP_CODE } from "../regex/constants"
import { buildSchema } from "./util/build-schema"

const isDomestic = (country: string) => USA_COUNTRY_CODE === country

const stateRegionSchema = string().when(
  ["availableSubdivisions"],
  ([availableSubdivisions], fieldSchema) => {
    if (isNilOrEmpty(availableSubdivisions)) {
      return fieldSchema.optional()
    }
    return fieldSchema.required().test(
      // Helps ensure leftover values on country switches are validated
      "isOneOfAvailableSubdivisions",
      "yup.common.mixed.required",
      (value) =>
        availableSubdivisions.some(
          (subdivision: { value: string; label: string }) =>
            subdivision.value === value
        )
    )
  }
)

// Allowed in an address line: Unicode letters/marks/numbers, whitespace, and
// address punctuation. Both the ASCII (') and typographic (’) apostrophes are
// permitted (e.g. O'Brien). `disallowedAddressChar` is the negation, used to
// find the first offending character so it can be surfaced in the error.
const ADDRESS_LINE_ALLOWED_CHARS = String.raw`\p{L}\p{M}\p{N}\s.,\-/#'’&():°`
const disallowedAddressChar = new RegExp(
  `[^${ADDRESS_LINE_ALLOWED_CHARS}]`,
  "u"
)

/**
 * Applies the address-line character allow-list to a string field. On failure
 * the error names the first disallowed character via the `{character}` value.
 */
function addressLineFormat(fieldSchema: StringSchema) {
  return fieldSchema.test("addressLineFormat", (value, context) => {
    const character = value?.match(disallowedAddressChar)?.[0]
    if (character === undefined) {
      return true
    }
    return context.createError({
      message: {
        key: "yup.address.invalidCharacter",
        values: { character },
      },
    })
  })
}

const schema = object({
  city: addressLineFormat(string()).required(),
  country: string().required().oneOf(Array.from(ALL_COUNTRIES)),
  postalCode: string().when("country", {
    is: isDomestic,
    then: (fieldSchema) =>
      fieldSchema.required().matches(ZIP_CODE, {
        message: "yup.address.postalCode",
        excludeEmptyString: true,
      }),
    otherwise: (fieldSchema) => fieldSchema.nullable(),
  }),
  availableSubdivisions: array()
    .of(object({ value: string(), label: string() }))
    .default([]), // This field is updated after GET /zero-hash-subdivisions is called
  stateRegion: stateRegionSchema,
  streetOne: addressLineFormat(string().trim()).required(),
  streetTwo: addressLineFormat(string().trim()).nullable(),
})

export const addressSchema = buildSchema("Address", schema)
